import { createMemo } from "solid-js";
import type { ListWithMeta } from "@listto/shared";
import type { useTheme } from "@/providers/theme-provider";
import { getBackToRouteForPath, getGlowColors, getListContextIdForPath } from "../helpers";

interface AppThemeStateOptions {
  committedPath: () => string;
  listsData: () => ListWithMeta[] | undefined;
  theme: ReturnType<typeof useTheme>;
}

export function useAppThemeState(options: AppThemeStateOptions) {
  const listContextId = createMemo(() => getListContextIdForPath(options.committedPath()));

  const activeTodoId = createMemo(() => {
    const path = options.committedPath();
    const todoMatch = /\/todo\/([^/]+)/.exec(path);
    return todoMatch?.[1] ?? null;
  });

  // by the navigate/Link wrapper at the consuming call site.
  const backToRoute = createMemo<string>(() => getBackToRouteForPath(options.committedPath()));

  const backParams = createMemo(() => {
    const path = options.committedPath();
    const listMatch = /\/lists\/([^/]+)/.exec(path);
    if (listMatch?.[1]) {
      return { listId: listMatch[1] };
    }
    return undefined;
  });

  const globalTheme = createMemo(
    () => options.theme.allThemes()?.find((r) => r.target_id === "global") ?? null,
  );

  const activeListTheme = createMemo(() => {
    const listId = listContextId();
    if (!listId) {
      return null;
    }
    const rawThemes = options.theme.allThemes();
    return rawThemes?.find((thm) => thm.target_id === listId) ?? null;
  });

  const activeBlur = createMemo(() => {
    const listId = listContextId();
    if (listId) {
      const list = options.listsData()?.find((l) => l.id === listId);
      if (list?.is_blur !== undefined && list.is_blur !== null) {
        return list.is_blur !== 0;
      }
    }
    return options.theme.isBlur();
  });

  const activeListTokens = createMemo(() => {
    const listTheme = activeListTheme();
    if (!listTheme) {
      return null;
    }
    const parent = options.theme.tokens();
    // The mode is not passed: the provider's single resolver sets it.
    return options.theme.resolveScopedTokens(listTheme, {
      isBlurContext: activeBlur(),
      parentTokens: parent,
    });
  });

  const activeListBackground = createMemo(
    () => activeListTokens()?.background ?? options.theme.tokens()?.background,
  );

  const glows = createMemo(() =>
    getGlowColors(
      options.committedPath(),
      activeListTheme(),
      globalTheme(),
      options.theme.tokens(),
    ),
  );

  return {
    listContextId,
    activeTodoId,
    backToRoute,
    backParams,
    globalTheme,
    activeListTheme,
    activeBlur,
    activeListTokens,
    activeListBackground,
    glows,
  };
}
